import { useEffect, useState } from 'react';
import './SessionsPage.css';
import Loader from '../../components/Loader/Loader.jsx';
import { listSessions, revokeSession, logout } from '../../api/authAPI/authAPI.js';
import Pagination from '../../components/Pagination/Pagination.jsx';
import useLang from '../../hooks/use-lang/useLang.js';

const PAGE_SIZE = 6;

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseBrowser(userAgent) {
  if (!userAgent) return '—';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  return 'Browser';
}

function parseOS(userAgent) {
  if (!userAgent) return '—';
  if (userAgent.includes('Windows NT 10')) return 'Windows 10/11';
  if (userAgent.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (userAgent.includes('Windows NT 6.2')) return 'Windows 8';
  if (userAgent.includes('Windows NT 6.1')) return 'Windows 7';
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac OS X 14') || userAgent.includes('Mac OS X 15')) return 'macOS 14+';
  if (userAgent.includes('Mac OS X 13')) return 'macOS 13';
  if (userAgent.includes('Mac OS X 12')) return 'macOS 12';
  if (userAgent.includes('Mac OS X 11')) return 'macOS 11';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Android 14')) return 'Android 14';
  if (userAgent.includes('Android 13')) return 'Android 13';
  if (userAgent.includes('Android 12')) return 'Android 12';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iOS')) return 'iOS';
  if (userAgent.includes('Linux')) return 'Linux';
  return 'OS';
}

function SessionsPage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [page, setPage] = useState(1);

  function loadSessions() {
    setLoading(true);
    return listSessions()
      .then((data) => {
        setSessions(data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadSessions();
  }, []);

  const totalPages = Math.ceil(sessions.length / PAGE_SIZE);
  const safePage = Math.min(page, totalPages || 1);
  const pagedSessions = sessions.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  async function handleRevoke(id) {
    setRevoking(id);
    try {
      const result = await revokeSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (result.current) {
        logout();
        window.location.reload();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="sessions-page">
      <div className="page__container">
        <div className="sessions__header">
          <h1 className="sessions__title">{t('sessions.title')}</h1>
        </div>

        {loading && <Loader text={t('sessions.loading')} />}
        {!loading && error && <p className="sessions__empty">{t('common.error')}: {error}</p>}
        {!loading && !error && sessions.length === 0 && (
          <p className="sessions__empty">{t('sessions.empty')}</p>
        )}

        {!loading && !error && sessions.length > 0 && (
          <div className="sessions__list">
            {pagedSessions.map((session) => (
              <div key={session.id} className="session-card">
                <div className="session-card__head">
                  <div className="session-card__browser">
                    <span className="session-card__browser-name">{parseBrowser(session.user_agent)}</span>
                    <span className="session-card__os">{parseOS(session.user_agent)}</span>
                  </div>
                  <button
                    className="session-card__revoke"
                    type="button"
                    onClick={() => handleRevoke(session.id)}
                    disabled={revoking === session.id}
                  >
                    {revoking === session.id ? t('sessions.revoking') : t('sessions.revoke')}
                  </button>
                </div>
                <div className="session-card__info">
                  <div className="session-card__row">
                    <span className="session-card__label">IP</span>
                    <span className="session-card__value">{session.ip || '—'}</span>
                  </div>
                  <div className="session-card__row">
                    <span className="session-card__label">{t('common.created')}</span>
                    <span className="session-card__value">{formatDateTime(session.created_at)}</span>
                  </div>
                  <div className="session-card__row">
                    <span className="session-card__label">{t('common.expires')}</span>
                    <span className="session-card__value">{formatDateTime(session.expires_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && (
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </div>
  );
}

export default SessionsPage;
